import React, { FC, useCallback, useMemo } from 'react'
import { Checkbox, Label } from '@mondra/ui-components'

import InputItem from '../../../components/InputItem'

import { sortDropdown } from '../../../shared/formFunctions'
import { IGpcAttribute, IGpcBrick, Dropdown } from '../../../components/AttributeSelector/types'
import AttributeSelector from '../../../components/AttributeSelector/AttributeSelector'

interface IGenericProductInfo {
  formik: any
  productTypes: any[]
  productionSystems: any[]
  brick: IGpcBrick | undefined
  groupOptions: Dropdown[]
  categoryOptions: Dropdown[]
  functionalUnit: Dropdown[]
}

export const GenericProductInfo: FC<IGenericProductInfo> = ({
  formik,
  productTypes,
  brick,
  groupOptions,
  categoryOptions,
  functionalUnit,
  productionSystems,
}) => {
  const formProductType = formik.values.productType

  const setGpcAttributes = useCallback(
    (attrs?: IGpcAttribute[]) => {
      if (attrs && attrs[0]) {
        const { gpcValueId, gpcValueName, gpcAttributeId, gpcAttributeName } = attrs[0]

        formik.setValues(prev => ({
          ...prev,
          gpcValueId,
          gpcValueName,
          gpcAttributeId,
          gpcAttributeName,
        }))
      } else {
        formik.setValues(prev => ({
          ...prev,
          gpcValueId: undefined,
          gpcValueName: undefined,
          gpcAttributeId: undefined,
          gpcAttributeName: undefined,
        }))
      }
    },
    [formik]
  )

  const gpcAttributes: IGpcAttribute[] = useMemo(
    () => [
      {
        gpcAttributeId: formik.values.gpcAttributeId,
        gpcAttributeName: formik.values.gpcAttributeName,
        gpcValueId: formik.values.gpcValueId,
        gpcValueName: formik.values.gpcValueName,
      },
    ],
    [
      formik.values.gpcAttributeId,
      formik.values.gpcAttributeName,
      formik.values.gpcValueId,
      formik.values.gpcValueName,
    ]
  )

  const handleProductTypeChange = useCallback(
    (e: React.ChangeEvent<Element>) => {
      const value = +(e.target as any).value

      setGpcAttributes(undefined)

      formik.setValues(prev => ({
        ...prev,
        productCategoryId: null,
        productGroupId: null,
        productId: null,
        productType: value,
      }))
    },
    [formik, setGpcAttributes]
  )

  const handleCategoryChange = useCallback(
    (e: React.ChangeEvent<Element>) => {
      const value = +(e.target as any).value

      setGpcAttributes(undefined)

      formik.setValues(prev => ({
        ...prev,
        productCategoryId: value,
        productGroupId: null,
        productId: null,
      }))
    },
    [formik, setGpcAttributes]
  )

  const handleProductGroupChange = useCallback(
    (e: React.ChangeEvent<Element>) => {
      const value = +(e.target as any).value

      setGpcAttributes(undefined)

      formik.setValues(prev => ({
        ...prev,
        productGroupId: value,
        productId: null,
      }))
    },
    [formik, setGpcAttributes]
  )

  const handleFunctionalUnitChange = useCallback(
    (e: React.ChangeEvent<Element>) => {
      const value = +(e.target as any).value

      setGpcAttributes(undefined)

      formik.setFieldValue('productId', value)
    },
    [formik, setGpcAttributes]
  )

  return (
    <div className="piece-content-inputs mb-5">
      <InputItem
        name="Generic Name"
        description="Please specify the name of this entry"
        type="input"
        onChange={formik.handleChange}
        formikName="name"
        showAsterisk
        formik={formik}
        defaultValue={formik.values.name}
      />
      <InputItem
        name="Product type"
        description="Please specify the product type of this entry"
        type="select"
        formikName="productType"
        create
        formik={formik}
        showAsterisk
        defaultValue={formProductType}
        onChange={handleProductTypeChange}
        options={sortDropdown(productTypes, 0, 'description')}
      />
      <InputItem
        name="Category"
        description=""
        type="select"
        formik={formik}
        showAsterisk
        disabled={categoryOptions.length === 0}
        defaultValue={formik.values.productCategoryId}
        onChange={handleCategoryChange}
        options={categoryOptions.map(el => ({
          label: el.name,
          value: el.id,
        }))}
        formikName="productCategoryId"
      />
      <div className="inputItem mt-6 flex h-full gap-2">
        <h3>Is BOM ?</h3>
        <Checkbox
          id="isBom"
          key="isBom"
          checked={formik.values.isBom}
          onChange={e => {
            formik.setFieldValue('isBom', e.currentTarget.checked)
            formik.handleChange(e)
          }}
          size={32}
        />
      </div>
      <InputItem
        name="Reference information"
        description=""
        type="text"
        className="mr-9 flex-1 [&>textarea]:!mb-0"
        onChange={formik.handleChange}
        formikName="referenceInformation"
        formik={formik}
        defaultValue={formik.values.referenceInformation || ''}
      />
      {/* Do not show Product Group for special ingredients */}
      {formProductType !== 3 && (
        <InputItem
          name="Product Group"
          description=""
          type="select"
          formik={formik}
          showAsterisk
          disabled={groupOptions.length === 0}
          defaultValue={formik.values.productGroupId}
          onChange={handleProductGroupChange}
          options={groupOptions.map(el => ({
            label: el.name,
            value: el.id,
          }))}
          formikName="productGroupId"
        />
      )}
      <InputItem
        name={
          formProductType === 1
            ? 'Brick Name'
            : formProductType === 2 || formProductType === 0
            ? 'Processed Ingredient Name'
            : 'Special Ingredient Name'
        }
        description=""
        type="select"
        defaultValue={formik.values.productId}
        formik={formik}
        showAsterisk
        disabled={functionalUnit.length === 0}
        onChange={handleFunctionalUnitChange}
        formikName="productId"
        options={functionalUnit.map(el => ({
          label: el.name,
          value: el.id,
        }))}
      />
      {formProductType === 0 && (
        <InputItem
          name="Production System"
          description="Please select the production system for this record."
          type="select"
          formik={formik}
          showAsterisk
          disabled={productionSystems.length === 0}
          defaultValue={formik.values.productionSystem}
          onChange={formik.handleChange}
          options={productionSystems.map(el => ({
            label: el.name,
            value: el.id,
          }))}
          formikName="productionSystem"
        />
      )}
      {brick && (
        <AttributeSelector
          brick={brick}
          attributes={brick.attributes}
          gpcAttributes={gpcAttributes}
          setGpcAttributes={setGpcAttributes}
          isSingleSelection
        />
      )}
    </div>
  )
}

export default GenericProductInfo
